/**
 * @module lib/format
 * Utilities for formatting text.
 */

import React from "react";
import moment from "moment";

/**
 * nl2br - replaces \n with jsx br tags, for formatting and display
 *
 * @param  {String} nlstring string containing newlines
 * @returns {Array}
 */
export function nl2br( nlstring ){
  // use the capture groups in split to return both the split segments and the newlines themselves
  const newlineRegex = /(\r\n|\r|\n)/g;

  // Now loop over the segments and replace the newlines with jsx <br />
  return nlstring.split(newlineRegex).map(function(line, index) {
    if (line.match(newlineRegex)) {
      return <br key={index} />;
    }
    return line;
  });
};

/**
 * pluralise - pluralises a word based on a value
 * pluralise(2, "chicken") == "chickens"
 * pluralise(252, "goose", "geese") == "geese"
 *
 * @param  {Number} value                unit value
 * @param  {String} base_unit            singular word for your unit e.g. hour, day
 * @param  {String} base_unit_pluralised optional plural form of unit (override)
 * @returns {String}                     formatted plural word
 */
export function pluralise(value, base_unit, base_unit_pluralised = base_unit + "s") {
    if ( value > 1 ) {
        return base_unit_pluralised;
    } else {
        return base_unit;
    }

}

/**
 * formatTimeAgo - format a moment date according to the time from now (past)
 * returns strings like "Now", "5 minutes ago", "12:00, Yesterday"
 *
 * @param  {type} date date as moment
 * @returns {String}     formatted timeAgo string
 */
export function timeAgo(inputDate, timezoneOffset = "+00:00") {
  // concatenate inputDate and timezone to form complete date:
  // TODO: consider seperating into utils  - one that accepts a moment, other accepts string
  const date = moment(inputDate + timezoneOffset);
  if( moment().diff(date, "seconds") < 60 ) {
      return "Now";
  }

  // 1 minute ago, 59 minutes ago
  const minuteDelta = moment().diff(date, "minutes");
  if ( minuteDelta < 60) {
      return `${minuteDelta} ${pluralise(minuteDelta, "minute")} ago`;
  }


  // If this was yesterday, this takes precedence
  // e.g 12 hours before 1am should say "13:00, Yesterday", not "12 hours ago"
  const dayDelta = moment().diff(date, "days");
  if ( dayDelta === 1 ) {
      return date.format("HH:mm[, Yesterday]");
  }

  // Otherwise we use the hour delta
  // 1 Hour ago, 3 Hours ago
  const hourDelta = moment().diff(date, "hours");
  if ( hourDelta < 24 ) {
      return `${hourDelta} ${pluralise(hourDelta, "hour")} ago`;
  }

  // Date within a week(exclusive, starting today)
  // 13:25, Wednesday
  if ( dayDelta < 7 ) {
      return date.format("HH:mm, dddd");
  }
  // Otherwise, default to full date display
  return date.format("HH:mm, DD MMMM YYYY");
}

/**
 * format a date with weekday, day of month, month, and year
 * weekday name and month name are abbreviated to three letters
 *
 * @param {date} inputDate - can be in any format compatible with moment.js
 * @returns {String} formatted date string
 */
export function formatLongDate(inputDate) {
  return moment(inputDate).format("ddd Do MMM YYYY");
}

/**
 * format a date numerical day, month and year: DD/MM/YY
 *
 * @param {date} inputDate - can be in any format compatible with moment.js
 * @returns {String} formatted date string
 */
export function formatShortDate(inputDate) {
  return moment(inputDate).format("DD/MM/YY");
}

/**
 * studentFullName - format a student's full name by combining their first and
 *   last names
 *
 * @param {string} firstName
 * @param {string} lastName
 * @returns {string} the student's full name
 */
export function studentFullName(firstName, lastName) {
  return firstName + " " + lastName;
}

/**
 * Map a Y/N to a true/false
 * @param {string} flag
 */
export function flagToBoolean(flag) {
  return flag.toUpperCase() === "Y";
}



// WEBPACK FOOTER //
// ./src/lib/format/index.js